import { useRef, useCallback } from "react";
import { io, Socket } from "socket.io-client";
import { Room, RemoteParticipant, RemoteVideoTrack } from "livekit-client";
import {
  useHeyGenStore,
  LiveKitConnectionData,
} from "../stores/heygenStore";
import { useAppStore } from "../stores/appStore";
import {
  AI_DIALOGUE_START_TIMEOUT_MS,
  mapResponseError,
  OFFLINE_SESSION_ERROR,
  isBrowserOffline,
  type LiveSpeechSessionError,
} from "../types/liveSpeechSessionError";
import { parseLiveTranscriptPayload } from "../types/liveTranscript";
import { getAuthToken } from "../utils/authToken";

interface UseLiveKitConnectionProps {
  auth?: {
    [key: string]: any;
  };
  videoRef: React.MutableRefObject<HTMLVideoElement | null>;
  audioStreamRef: React.MutableRefObject<MediaStream | null>;
  remoteTracksRef: React.MutableRefObject<Array<{ track: any; kind: string }>>;
  setIsConnecting: (connecting: boolean) => void;
  setIsConnected: (connected: boolean) => void;
  setError: (error: string | null) => void;
  setLiveKitData: (data: LiveKitConnectionData | null) => void;
  setAudioStream: (stream: MediaStream | null) => void;
  setSessionActive: (active: boolean) => void;
  setLessonSecondsAllowed: (seconds: number | null) => void;
  setLessonSecondsRemaining: (seconds: number | null) => void;
  setWindowTitle: (title: string | null) => void;
  onError?: (error: string) => void;
  onSessionError?: (error: LiveSpeechSessionError) => void;
  onRemoteVideoLost?: () => void;
  setupRemoteAudioAnalysis: (
    audioTrack: any,
    options?: {
      onAiSpeechDetected?: () => void;
      onAiSpeakingStateChange?: (speaking: boolean) => void;
    },
  ) => void;
}

export const useLiveKitConnection = ({
  auth,
  videoRef,
  audioStreamRef,
  remoteTracksRef,
  setIsConnecting,
  setIsConnected,
  setError,
  setLiveKitData,
  setAudioStream,
  setLessonSecondsAllowed,
  setLessonSecondsRemaining,
  setWindowTitle,
  onError,
  onSessionError,
  onRemoteVideoLost,
  setupRemoteAudioAnalysis,
}: UseLiveKitConnectionProps) => {
  const roomRef = useRef<Room | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const connectToLiveKitRef = useRef<
    ((data: LiveKitConnectionData) => Promise<void>) | null
  >(null);
  const onErrorRef = useRef<((error: string) => void) | undefined>(onError);
  const onSessionErrorRef = useRef(onSessionError);
  const onRemoteVideoLostRef = useRef(onRemoteVideoLost);
  const intentionalDisconnectRef = useRef(false);
  const sessionErrorActiveRef = useRef(false);
  const gotLiveKitRef = useRef(false);
  const userHasSpokenRef = useRef(false);
  const responseWatchRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const videoLostCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const hadRemoteVideoRef = useRef(false);

  onSessionErrorRef.current = onSessionError;
  onRemoteVideoLostRef.current = onRemoteVideoLost;

  const clearResponseWatch = useCallback(() => {
    if (responseWatchRef.current) {
      clearTimeout(responseWatchRef.current);
      responseWatchRef.current = null;
    }
  }, []);

  const clearVideoLostCloseTimer = useCallback(() => {
    if (videoLostCloseTimerRef.current) {
      clearTimeout(videoLostCloseTimerRef.current);
      videoLostCloseTimerRef.current = null;
    }
  }, []);

  const scheduleCloseOnVideoLost = useCallback(() => {
    if (intentionalDisconnectRef.current || !hadRemoteVideoRef.current) {
      return;
    }
    clearVideoLostCloseTimer();
    // Short delay so a brief renegotiate does not close the dialog.
    videoLostCloseTimerRef.current = setTimeout(() => {
      videoLostCloseTimerRef.current = null;
      if (intentionalDisconnectRef.current) return;
      const stillHasVideo = remoteTracksRef.current.some(
        (item) => item.kind === "video",
      );
      if (stillHasVideo) return;
      onRemoteVideoLostRef.current?.();
    }, 800);
  }, [clearVideoLostCloseTimer, remoteTracksRef]);

  const clearSessionError = useCallback(() => {
    sessionErrorActiveRef.current = false;
  }, []);

  const transcriptHandlerRef = useRef<((payload: unknown) => void) | null>(
    null,
  );

  const detachTranscriptListener = useCallback((socket: Socket | null) => {
    if (!socket || !transcriptHandlerRef.current) return;
    socket.off("transcript", transcriptHandlerRef.current);
    transcriptHandlerRef.current = null;
  }, []);

  const prepareDisconnect = useCallback(() => {
    intentionalDisconnectRef.current = true;
    clearResponseWatch();
    clearVideoLostCloseTimer();
    detachTranscriptListener(socketRef.current);
    useHeyGenStore.getState().clearLiveTranscript();
    useAppStore.getState().setRealtimeDetailsWindowText(null);
  }, [clearResponseWatch, clearVideoLostCloseTimer, detachTranscriptListener]);

  const reportSessionError = useCallback(
    (error: LiveSpeechSessionError) => {
      if (sessionErrorActiveRef.current) return;
      sessionErrorActiveRef.current = true;
      clearResponseWatch();
      setIsConnecting(false);
      setError(error.message);
      try {
        onSessionErrorRef.current?.(error);
      } catch (e) {
        console.warn("onSessionError callback failed:", e);
      }
    },
    [clearResponseWatch, setError, setIsConnecting],
  );

  const armResponseWatch = useCallback(() => {
    // Don't re-arm on socket reconnect/upgrade after the avatar session is ready.
    if (gotLiveKitRef.current || sessionErrorActiveRef.current) {
      return;
    }
    clearResponseWatch();
    responseWatchRef.current = setTimeout(() => {
      if (!gotLiveKitRef.current && !sessionErrorActiveRef.current) {
        const userNeverSpoke = !userHasSpokenRef.current;
        reportSessionError({
          code: "ai_dialogue_timeout",
          message: userNeverSpoke
            ? "Please start speaking to begin the conversation."
            : "The session didn't start in time. Please try again or end the session.",
          retryable: true,
          canFallbackAudioOnly: true,
        });
      }
    }, AI_DIALOGUE_START_TIMEOUT_MS);
  }, [clearResponseWatch, reportSessionError]);

  const markLiveKitSessionReady = useCallback(() => {
    if (gotLiveKitRef.current) return;
    gotLiveKitRef.current = true;
    clearResponseWatch();
  }, [clearResponseWatch]);

  const markUserSpoken = useCallback(() => {
    userHasSpokenRef.current = true;
  }, []);

  const getAudioStream = useCallback(async (): Promise<MediaStream | null> => {
    try {
      const storeStream = useHeyGenStore.getState().audioStream;
      if (storeStream) {
        const activeTracks = storeStream
          .getAudioTracks()
          .filter((track) => track.readyState === "live");
        if (activeTracks.length > 0) {
          audioStreamRef.current = storeStream;
          return storeStream;
        }
        storeStream.getTracks().forEach((track) => {
          try {
            if (track.readyState !== "ended") {
              track.stop();
            }
          } catch (err) {
            console.warn("Error stopping store track:", err);
          }
        });
        useHeyGenStore.getState().setAudioStream(null);
      }

      if (audioStreamRef.current) {
        const activeTracks = audioStreamRef.current
          .getAudioTracks()
          .filter((track) => track.readyState === "live");
        if (activeTracks.length > 0) {
          setAudioStream(audioStreamRef.current);
          return audioStreamRef.current;
        }
        audioStreamRef.current.getTracks().forEach((track) => {
          try {
            if (track.readyState !== "ended") {
              track.stop();
            }
          } catch (err) {
            console.warn("Error stopping old track:", err);
          }
        });
        audioStreamRef.current = null;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      audioStreamRef.current = stream;
      setAudioStream(stream);

      (window as any).__heygenAudioRefStream__ = stream;

      return stream;
    } catch (err) {
      setError("Failed to access microphone");
      onError?.("Failed to access microphone");
      return null;
    }
  }, [setAudioStream, setError, onError, audioStreamRef]);

  const connectToLiveKit = useCallback(
    async (connectionData: LiveKitConnectionData) => {
      connectToLiveKitRef.current = connectToLiveKit;
      if (!connectionData.token || !connectionData.url) {
        setError("Invalid LiveKit connection data");
        return;
      }

      setIsConnecting(true);
      setError(null);
      hadRemoteVideoRef.current = false;
      clearVideoLostCloseTimer();

      try {
        const room = new Room();

        room.on("connected" as any, () => {
          setIsConnected(true);
          setIsConnecting(false);
        });

        room.on("disconnected" as any, () => {
          setIsConnected(false);
          setIsConnecting(false);
          if (!intentionalDisconnectRef.current) {
            reportSessionError({
              code: "disconnect",
              message: "Connection lost. You can retry or end the session.",
              retryable: true,
              canFallbackAudioOnly: true,
            });
          }
        });

        room.on(
          "trackSubscribed" as any,
          (track: any, _publication: any, _participant: any) => {
            remoteTracksRef.current.push({ track, kind: track.kind });

            if (track.kind === "video") {
              const videoTrack = track as RemoteVideoTrack;
              hadRemoteVideoRef.current = true;
              clearVideoLostCloseTimer();
              if (videoRef.current) {
                videoTrack.attach(videoRef.current);
              } else {
                const retryInterval = setInterval(() => {
                  if (videoRef.current) {
                    videoTrack.attach(videoRef.current);
                    clearInterval(retryInterval);
                  }
                }, 100);
                setTimeout(() => clearInterval(retryInterval), 5000);
              }
            } else if (track.kind === "audio") {
              const audioTrack = track as any;

              if (videoRef.current) {
                audioTrack.attach(videoRef.current);
              } else {
                const retryInterval = setInterval(() => {
                  if (videoRef.current) {
                    audioTrack.attach(videoRef.current);
                    clearInterval(retryInterval);
                  }
                }, 100);
                setTimeout(() => clearInterval(retryInterval), 5000);
              }

              setupRemoteAudioAnalysis(audioTrack, {
                onAiSpeechDetected: markLiveKitSessionReady,
              });
            }
          },
        );

        room.on("trackUnsubscribed" as any, (track: any) => {
          remoteTracksRef.current = remoteTracksRef.current.filter(
            (item) => item.track !== track,
          );

          if (track.kind === "video") {
            const videoTrack = track as RemoteVideoTrack;
            videoTrack.detach();
            scheduleCloseOnVideoLost();
          } else if (track.kind === "audio") {
            const audioTrack = track as any;
            audioTrack.detach();
          }
        });

        room.on(
          "participantDisconnected" as any,
          (_participant: RemoteParticipant) => {
            if (intentionalDisconnectRef.current) return;
            window.setTimeout(() => {
              if (intentionalDisconnectRef.current) return;
              const roomNow = roomRef.current;
              if (!roomNow || roomNow.remoteParticipants.size === 0) {
                scheduleCloseOnVideoLost();
              }
            }, 300);
          },
        );

        await room.connect(connectionData.url, connectionData.token);

        roomRef.current = room;

        const currentStream = audioStreamRef.current;
        if (currentStream && (room as any).localParticipant) {
          const audioTrack = currentStream.getAudioTracks()[0];
          if (audioTrack) {
            try {
              await (room as any).localParticipant.publishTrack(audioTrack, {
                source: "microphone",
              });
            } catch {
              // Failed to publish audio track
            }
          }
        }
      } catch (err: any) {
        const errorMessage = err.message || "Failed to connect to LiveKit";
        setError(errorMessage);
        setIsConnecting(false);
        onError?.(errorMessage);
        reportSessionError({
          code: "realtime_error",
          message: errorMessage,
          retryable: true,
          canFallbackAudioOnly: true,
        });
      }
    },
    [
      setIsConnecting,
      setIsConnected,
      setError,
      onError,
      reportSessionError,
      markLiveKitSessionReady,
      setupRemoteAudioAnalysis,
      scheduleCloseOnVideoLost,
      clearVideoLostCloseTimer,
      videoRef,
      remoteTracksRef,
      audioStreamRef,
    ],
  );

  const initializeSocket = useCallback(() => {
    if (socketRef.current && socketRef.current.connected) {
      return;
    }

    if (!auth) {
      return;
    }

    const SOCKET_URL = process.env.REACT_APP_SOCKET_URL;

    if (socketRef.current) {
      intentionalDisconnectRef.current = true;
      detachTranscriptListener(socketRef.current);
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    const token = getAuthToken() ?? auth.token;
    if (!token) {
      return;
    }

    const authWithToken = { ...auth, token };

    intentionalDisconnectRef.current = false;
    sessionErrorActiveRef.current = false;
    gotLiveKitRef.current = false;
    userHasSpokenRef.current = false;
    useHeyGenStore.getState().clearLiveTranscript();
    const socket = io(SOCKET_URL, { auth: authWithToken });
    socketRef.current = socket;
    socket.auth = authWithToken;

    socket.on("connect", () => {
      const currentToken = getAuthToken() ?? auth.token;
      if (currentToken && socketRef.current) {
        socketRef.current.auth = { ...auth, token: currentToken };
      }
      armResponseWatch();

      // Same as audio roleplay: ask the backend to start the AI dialogue.
      if (auth.selling_dialogue || auth.lesson_dialogue) {
        socketRef.current?.emit("start_ai_dialogue", {
          ...auth,
          token: currentToken,
        });
      }
    });

    socket.on("disconnect", () => {
      if (intentionalDisconnectRef.current) return;
      reportSessionError({
        code: "disconnect",
        message: "Connection lost. You can retry or end the session.",
        retryable: true,
        canFallbackAudioOnly: true,
      });
    });

    socket.on("connect_error", (err: any) => {
      if (intentionalDisconnectRef.current) return;
      reportSessionError({
        ...OFFLINE_SESSION_ERROR,
        message:
          isBrowserOffline()
            ? OFFLINE_SESSION_ERROR.message
            : "No internet connection. Check your network and try again.",
      });
      console.error("Realtime socket connect_error:", err);
    });

    socket.on("realtime.error", (payload: any) => {
      const message =
        (typeof payload?.message === "string" && payload.message) ||
        (typeof payload?.error?.message === "string" &&
          payload.error.message) ||
        (typeof payload?.error === "string" && payload.error) ||
        "A realtime error occurred. You can retry or end the session.";
      reportSessionError({
        code: "realtime_error",
        message,
        retryable: true,
        canFallbackAudioOnly: true,
      });
    });

    socket.on("ai_dialogue_started", () => {
      markLiveKitSessionReady();
    });

    const handleLiveKitData = (data: LiveKitConnectionData) => {
      if (!data.token || !data.url) {
        setError("Invalid LiveKit connection data received");
        return;
      }
      // Keep the start timeout armed until avatar media actually arrives.
      setLiveKitData(data);
      connectToLiveKitRef.current?.(data);
    };

    const handleResponse = (msg: any) => {
      if ("seconds_allowed" in msg && typeof msg.seconds_allowed === "number") {
        setLessonSecondsAllowed(msg.seconds_allowed);
        setLessonSecondsRemaining(msg.seconds_allowed);
      }

      const windowTitle =
        msg.window_title ||
        msg.windowTitle ||
        msg?.data?.window_title ||
        msg?.data?.windowTitle;

      if (windowTitle) {
        setWindowTitle(windowTitle);
      }

      const detailsWindowText =
        msg.details_window_text ||
        msg.detailsWindowText ||
        msg?.data?.details_window_text ||
        msg?.data?.detailsWindowText;
      if (
        typeof detailsWindowText === "string" &&
        detailsWindowText.trim()
      ) {
        useAppStore
          .getState()
          .setRealtimeDetailsWindowText(detailsWindowText.trim());
      }

      const sessionIdRaw =
        msg.session_id ||
        msg.sessionId ||
        msg?.data?.session_id ||
        msg?.data?.sessionId;
      if (typeof sessionIdRaw === "string" && sessionIdRaw.trim()) {
        const nextSessionId = sessionIdRaw.trim();
        const store = useHeyGenStore.getState();
        if (
          store.liveTranscriptSessionId &&
          store.liveTranscriptSessionId !== nextSessionId
        ) {
          store.clearLiveTranscript();
        }
        useHeyGenStore.getState().setLiveTranscriptSessionId(nextSessionId);
      }

      const mapped = mapResponseError(msg?.error ?? msg?.data?.error);
      if (mapped) {
        if (mapped.code === "week_limit_exceeded") {
          intentionalDisconnectRef.current = true;
          detachTranscriptListener(socketRef.current);
          socketRef.current?.disconnect();
          socketRef.current = null;
          useHeyGenStore.getState().clearLiveTranscript();
        }
        reportSessionError(mapped);
        return;
      }

      const url =
        msg.livekit_url ||
        msg.livekitUrl ||
        msg?.data?.livekit_url ||
        msg?.data?.livekitUrl;

      const livekitToken =
        msg.livekit_client_token ||
        msg.livekitClientToken ||
        msg.token ||
        msg?.data?.livekit_client_token ||
        msg?.data?.livekitClientToken ||
        msg?.data?.token;

      const roomName =
        msg.room_name ||
        msg.room ||
        msg.livekit_room ||
        msg.roomName ||
        msg?.data?.room_name ||
        msg?.data?.room ||
        msg?.data?.livekit_room ||
        msg?.data?.roomName;

      if (!url || !livekitToken) {
        return;
      }

      handleLiveKitData({
        url,
        token: livekitToken,
        room_name: roomName,
      });
    };

    const handleError = (errorData: { error: string }) => {
      const mapped = mapResponseError(errorData?.error);
      if (mapped) {
        reportSessionError(mapped);
        return;
      }
      setError(errorData.error);
      onErrorRef.current?.(errorData.error);
    };

    const handleTranscript = (payload: unknown) => {
      // Ignore events from a socket that is no longer current.
      if (socketRef.current !== socket) return;

      const items = Array.isArray(payload) ? payload : [payload];
      const { applyLiveTranscriptLine } = useHeyGenStore.getState();
      for (const item of items) {
        // Support { type: "transcript", ...fields } wrappers if BE nests them.
        const candidate =
          item &&
          typeof item === "object" &&
          "data" in (item as object) &&
          (item as { data?: unknown }).data &&
          typeof (item as { data?: unknown }).data === "object"
            ? (item as { data: unknown }).data
            : item;
        const line = parseLiveTranscriptPayload(candidate);
        if (line) {
          applyLiveTranscriptLine(line);
        }
      }
    };

    transcriptHandlerRef.current = handleTranscript;

    socket.on("response", handleResponse);
    socket.on("heygen_livekit_data", handleLiveKitData);
    socket.on("heygen_error", handleError);
    socket.on("transcript", handleTranscript);
  }, [
    auth,
    setLiveKitData,
    setError,
    setLessonSecondsAllowed,
    setLessonSecondsRemaining,
    setWindowTitle,
    armResponseWatch,
    markLiveKitSessionReady,
    reportSessionError,
    detachTranscriptListener,
  ]);

  connectToLiveKitRef.current = connectToLiveKit;
  onErrorRef.current = onError;

  const retrySession = useCallback(async () => {
    if (isBrowserOffline()) {
      reportSessionError({
        ...OFFLINE_SESSION_ERROR,
        canFallbackAudioOnly: true,
      });
      return;
    }

    clearResponseWatch();
    sessionErrorActiveRef.current = false;
    gotLiveKitRef.current = false;
    // Keep userHasSpokenRef — Retry after an actual utterance still means
    // "session failed", not "please start speaking".
    intentionalDisconnectRef.current = false;
    setIsConnecting(false);
    setError(null);

    // Soft retry keeps the same backend/LiveKit session. Full teardown ends the
    // dialogue as "too short" and can leave the UI stuck on the connecting spinner.
    if (socketRef.current?.connected) {
      const currentToken = getAuthToken() ?? auth?.token;
      if (auth?.selling_dialogue || auth?.lesson_dialogue) {
        socketRef.current.emit("start_ai_dialogue", {
          ...(auth || {}),
          token: currentToken,
        });
      }
      armResponseWatch();
      return;
    }

    intentionalDisconnectRef.current = true;
    if (roomRef.current) {
      await roomRef.current.disconnect().catch(() => {});
      roomRef.current = null;
    }
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    setIsConnected(false);
    setLiveKitData(null);
    intentionalDisconnectRef.current = false;
    initializeSocket();
  }, [
    auth,
    armResponseWatch,
    clearResponseWatch,
    initializeSocket,
    reportSessionError,
    setError,
    setIsConnected,
    setIsConnecting,
    setLiveKitData,
  ]);

  return {
    connectToLiveKit,
    initializeSocket,
    getAudioStream,
    prepareDisconnect,
    clearSessionError,
    retrySession,
    markUserSpoken,
    roomRef,
    socketRef,
  };
};
